import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  SK_STIGMA_TRACKER_DRAFTS,
  SK_STIGMA_TRACKER_ENTRY_MODE,
  SK_STIGMA_TRACKER_CURRENT,
} from '../constants/storageKeys';
import { getDemoStigmaEntries } from '../demoData';
import type {
  StigmaTrackerEntry,
  StigmaTrackerStep1,
  StigmaTrackerStep2,
  StigmaTrackerStep3,
  StigmaTrackerStep4,
  StigmaTrackerDepth,
  EntryStatus,
} from '../appTypes';

// ── Stigma Tracker hook (2026-05-14 redesign) ────────────────────────
// Manages the Stigma Tracker's entry mode, step state, current entry,
// last-used mode, and localStorage sync. Mirrors the pattern used by
// useCheckin / useEDMode — owns state + persistence, exposes setters
// and convenience methods for the screen orchestrator to call.
//
// HSDF + CPHO anchored per design spec v3 at
// Working Documents/CareWeave/stigma-tracker-redesign/Final/.

export type EntryMode = 'doors' | 'quick' | 'walkthrough';
export type TrackerStep = 1 | 2 | 3 | 4 | 'review';

function generateEntryId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `entry-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function blankStep1(): StigmaTrackerStep1 {
  return { freeText: '', sliderValue: null, affectChips: [] };
}
function blankStep2(): StigmaTrackerStep2 {
  return { freeText: '', people: [], when: null, practiceChips: [], earnshawType: null };
}
function blankStep3(): StigmaTrackerStep3 {
  return { setting: [], namedForces: [], connectionFollowup: null, connectionFollowupText: '' };
}
function blankStep4(): StigmaTrackerStep4 {
  return { carriedChips: [] };
}

export function createBlankEntry(): StigmaTrackerEntry {
  const now = new Date().toISOString();
  return {
    id: generateEntryId(),
    status: 'draft',
    entryMode: 'walkthrough',
    createdAt: now,
    updatedAt: now,
    submittedAt: null,
    title: null,
    step1: blankStep1(),
    step2: blankStep2(),
    step3: blankStep3(),
    step4: blankStep4(),
    depth: null,
  };
}

function loadDrafts(): Record<string, StigmaTrackerEntry> {
  try {
    const raw = localStorage.getItem(SK_STIGMA_TRACKER_DRAFTS);
    return raw ? (JSON.parse(raw) as Record<string, StigmaTrackerEntry>) : {};
  } catch {
    return {};
  }
}

function saveDrafts(d: Record<string, StigmaTrackerEntry>): void {
  try {
    localStorage.setItem(SK_STIGMA_TRACKER_DRAFTS, JSON.stringify(d));
  } catch {
    /* quota exceeded — ignored */
  }
}

function loadCurrentEntry(): StigmaTrackerEntry | null {
  try {
    const raw = localStorage.getItem(SK_STIGMA_TRACKER_CURRENT);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StigmaTrackerEntry;
    if (!parsed.id || !parsed.status) return null;
    return parsed;
  } catch {
    return null;
  }
}

function saveCurrentEntry(e: StigmaTrackerEntry | null): void {
  try {
    if (e === null) {
      localStorage.removeItem(SK_STIGMA_TRACKER_CURRENT);
    } else {
      localStorage.setItem(SK_STIGMA_TRACKER_CURRENT, JSON.stringify(e));
    }
  } catch {
    /* ignored */
  }
}

function loadLastMode(): 'quick' | 'walkthrough' | null {
  try {
    const v = localStorage.getItem(SK_STIGMA_TRACKER_ENTRY_MODE);
    return v === 'quick' || v === 'walkthrough' ? v : null;
  } catch {
    return null;
  }
}

export interface UseStigmaTrackerReturn {
  entryMode: EntryMode;
  currentStep: TrackerStep;
  entry: StigmaTrackerEntry;
  lastUsedMode: 'quick' | 'walkthrough' | null;
  showDepth: boolean;
  drafts: StigmaTrackerEntry[];

  // Read-only viewing of a saved reflection (distinct from the editable `entry`)
  viewedEntry: StigmaTrackerEntry | null;
  viewEntry: (id: string) => void;
  closeViewedEntry: () => void;

  // Navigation
  goToDoors: () => void;
  chooseQuick: () => void;
  chooseWalkthrough: () => void;
  goToStep: (step: TrackerStep) => void;
  setShowDepth: (v: boolean) => void;

  // Per-step setters
  updateStep1: (next: StigmaTrackerStep1) => void;
  updateStep2: (next: StigmaTrackerStep2) => void;
  updateStep3: (next: StigmaTrackerStep3) => void;
  updateStep4: (next: StigmaTrackerStep4) => void;
  updateDepth: (next: StigmaTrackerDepth) => void;

  // Submit / persistence
  saveAndComeBackLater: () => void;     // status stays 'draft', persists, exits
  saveAsInProgress: () => void;         // status = 'in_progress', submittedAt = now
  saveAsComplete: () => void;           // status = 'complete', submittedAt = now
  startNewEntry: () => void;            // reset current to a fresh blank
  resumeEntry: (id: string) => void;    // load a draft / in_progress entry from drafts
  deleteEntry: (id: string) => void;
}

/**
 * useStigmaTracker — owns state for the redesigned Stigma Tracker.
 * Pattern follows useCheckin / useEDMode: state + localStorage sync internal,
 * setters and convenience methods exposed.
 */
export function useStigmaTracker(demoActive = false): UseStigmaTrackerReturn {
  const [entryMode, setEntryMode] = useState<EntryMode>('doors');
  const [currentStep, setCurrentStep] = useState<TrackerStep>(1);
  const [entry, setEntry] = useState<StigmaTrackerEntry>(() => {
    const stored = loadCurrentEntry();
    return stored ?? createBlankEntry();
  });
  const [lastUsedMode, setLastUsedMode] = useState<'quick' | 'walkthrough' | null>(() =>
    loadLastMode(),
  );
  const [showDepth, setShowDepth] = useState(false);
  const [draftsMap, setDraftsMap] = useState<Record<string, StigmaTrackerEntry>>(() => loadDrafts());
  const [viewedEntry, setViewedEntry] = useState<StigmaTrackerEntry | null>(null);
  // Demo reflections are generated in memory and merged for display only —
  // never written to localStorage. Removing one hides it for the session.
  const [dismissedDemoIds, setDismissedDemoIds] = useState<Set<string>>(() => new Set());
  const demoEntries = useMemo(
    () => (demoActive ? getDemoStigmaEntries().filter(e => !dismissedDemoIds.has(e.id)) : []),
    [demoActive, dismissedDemoIds],
  );

  // Persist current entry to localStorage whenever it changes (unless complete).
  // Complete entries live in drafts map; the "current" slot holds the editing entry.
  useEffect(() => {
    if (entry.status === 'complete') {
      saveCurrentEntry(null);
    } else {
      saveCurrentEntry(entry);
    }
  }, [entry]);

  // Auto-write entry to drafts map on every change so history reflects WIP.
  useEffect(() => {
    setDraftsMap(prev => {
      const next = {
        ...prev,
        [entry.id]: { ...entry, updatedAt: new Date().toISOString() },
      };
      saveDrafts(next);
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry]);

  const drafts = useMemo(
    () =>
      [...Object.values(draftsMap), ...demoEntries].sort(
        (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
      ),
    [draftsMap, demoEntries],
  );

  // Look up an entry by id across real drafts and merged demo entries.
  const findEntry = useCallback(
    (id: string): StigmaTrackerEntry | null =>
      draftsMap[id] ?? demoEntries.find(e => e.id === id) ?? null,
    [draftsMap, demoEntries],
  );

  // Open a saved reflection for read-only viewing. Reads from the drafts map so
  // viewing never touches the editable `entry` or reorders the list.
  const viewEntry = useCallback(
    (id: string) => {
      setViewedEntry(findEntry(id));
    },
    [findEntry],
  );

  const closeViewedEntry = useCallback(() => {
    setViewedEntry(null);
  }, []);

  const goToDoors = useCallback(() => {
    setEntryMode('doors');
  }, []);

  const chooseQuick = useCallback(() => {
    setEntryMode('quick');
    setLastUsedMode('quick');
    try {
      localStorage.setItem(SK_STIGMA_TRACKER_ENTRY_MODE, 'quick');
    } catch {
      /* ignored */
    }
    setEntry(prev => ({ ...prev, entryMode: 'quick' }));
  }, []);

  const chooseWalkthrough = useCallback(() => {
    setEntryMode('walkthrough');
    setLastUsedMode('walkthrough');
    try {
      localStorage.setItem(SK_STIGMA_TRACKER_ENTRY_MODE, 'walkthrough');
    } catch {
      /* ignored */
    }
    setCurrentStep(1);
    setEntry(prev => ({ ...prev, entryMode: 'walkthrough' }));
  }, []);

  const goToStep = useCallback((step: TrackerStep) => {
    setCurrentStep(step);
  }, []);

  const updateStep1 = useCallback((next: StigmaTrackerStep1) => {
    setEntry(prev => ({
      ...prev,
      step1: next,
      status: prev.status === 'complete' ? 'complete' : 'draft',
    }));
  }, []);

  const updateStep2 = useCallback((next: StigmaTrackerStep2) => {
    setEntry(prev => ({
      ...prev,
      step2: next,
      status: prev.status === 'complete' ? 'complete' : 'draft',
    }));
  }, []);

  const updateStep3 = useCallback((next: StigmaTrackerStep3) => {
    setEntry(prev => ({
      ...prev,
      step3: next,
      status: prev.status === 'complete' ? 'complete' : 'draft',
    }));
  }, []);

  const updateStep4 = useCallback((next: StigmaTrackerStep4) => {
    setEntry(prev => ({
      ...prev,
      step4: next,
      status: prev.status === 'complete' ? 'complete' : 'draft',
    }));
  }, []);

  const updateDepth = useCallback((next: StigmaTrackerDepth) => {
    setEntry(prev => ({
      ...prev,
      depth: next,
      status: prev.status === 'complete' ? 'complete' : 'draft',
    }));
  }, []);

  /**
   * Save the current entry as a draft (auto-persisted via useEffect) and
   * return the user to the entry doors. Status remains 'draft'.
   */
  const saveAndComeBackLater = useCallback(() => {
    // Persistence is already handled by useEffect; this method exists for
    // semantic clarity at the call site and to provide a hook for future
    // analytics / toast confirmations.
    setEntryMode('doors');
  }, []);

  const setStatusAndSubmit = useCallback((nextStatus: 'in_progress' | 'complete') => {
    setEntry(prev => {
      const submittedAt = new Date().toISOString();
      return { ...prev, status: nextStatus as EntryStatus, submittedAt, updatedAt: submittedAt };
    });
  }, []);

  const saveAsInProgress = useCallback(() => {
    setStatusAndSubmit('in_progress');
  }, [setStatusAndSubmit]);

  const saveAsComplete = useCallback(() => {
    setStatusAndSubmit('complete');
  }, [setStatusAndSubmit]);

  const startNewEntry = useCallback(() => {
    const fresh = createBlankEntry();
    setEntry(fresh);
    setEntryMode('doors');
    setCurrentStep(1);
    setShowDepth(false);
  }, []);

  const resumeEntry = useCallback(
    (id: string) => {
      const target = findEntry(id);
      if (!target) return;
      setEntry(target);
      setEntryMode(target.entryMode === 'quick' ? 'quick' : 'walkthrough');
      setCurrentStep(1);
      setShowDepth(false);
    },
    [findEntry],
  );

  const deleteEntry = useCallback(
    (id: string) => {
      // Demo entries are not in the persisted map; hide them for the session.
      if (!(id in draftsMap)) {
        setDismissedDemoIds(prev => {
          const next = new Set(prev);
          next.add(id);
          return next;
        });
        if (viewedEntry?.id === id) setViewedEntry(null);
        return;
      }
      setDraftsMap(prev => {
        const { [id]: _removed, ...rest } = prev;
        saveDrafts(rest);
        return rest;
      });
      if (entry.id === id) {
        const fresh = createBlankEntry();
        setEntry(fresh);
      }
    },
    [draftsMap, entry.id, viewedEntry],
  );

  return {
    entryMode,
    currentStep,
    entry,
    lastUsedMode,
    showDepth,
    drafts,

    viewedEntry,
    viewEntry,
    closeViewedEntry,

    goToDoors,
    chooseQuick,
    chooseWalkthrough,
    goToStep,
    setShowDepth,

    updateStep1,
    updateStep2,
    updateStep3,
    updateStep4,
    updateDepth,

    saveAndComeBackLater,
    saveAsInProgress,
    saveAsComplete,
    startNewEntry,
    resumeEntry,
    deleteEntry,
  };
}
